import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Generates and downloads a PDF from the static deviation/change-control report content.
 * Renders an off-screen HTML element, captures it with html2canvas, and saves via jsPDF.
 *
 * @param {'deviation'|'change-control'} docType
 * @param {string} [fileName] - optional override for the downloaded file name
 */
export const downloadReportPDF = async (docType, fileName) => {
  const isDeviation = docType === 'deviation';
  const defaultName = isDeviation ? 'Deviation_Report.pdf' : 'Change_Controls_Report.pdf';

  // Build the HTML content
  const html = isDeviation ? buildDeviationHTML() : buildChangeControlHTML();

  // Create off-screen container
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;width:700px;background:#fff;padding:24px;font-family:Arial,sans-serif;font-size:13px;color:#222;';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    const pageHeight = pdf.internal.pageSize.getHeight();
    let position = 0;
    while (position < pdfHeight) {
      if (position > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, -position, pdfWidth, pdfHeight);
      position += pageHeight;
    }
    pdf.save(fileName || defaultName);
  } finally {
    document.body.removeChild(container);
  }
};

const sectionStyle = 'font-weight:700;margin:10px 0 4px;font-size:13px;';
const fieldRowStyle = 'display:flex;gap:8px;margin-bottom:3px;font-size:12px;';
const fieldLabelStyle = 'min-width:150px;color:#444;';
const redBoxStyle = 'border:1px solid #d32f2f;padding:1px 6px;border-radius:2px;color:#d32f2f;font-size:12px;display:inline-block;';

function buildDeviationHTML() {
  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
      <div><div style="font-size:16px;font-weight:700;">Oxford Biomedica</div><div style="font-size:10px;color:#888;">Corporate</div></div>
      <div style="font-size:12px;color:#1976d2;font-weight:600;">Oxford Biomedica</div>
    </div>
    <div style="font-size:11px;color:#555;margin-bottom:6px;">Reference SOP No. &amp; Title: 99-03, Handling of deviations</div>
    <div style="text-align:center;font-weight:700;font-size:14px;margin-bottom:10px;">DEVIATION REPORT</div>
    <hr style="border:none;border-top:1px solid #ccc;margin-bottom:8px;"/>

    <div style="display:flex;justify-content:space-between;margin-bottom:8px;">
      <span style="${redBoxStyle}">DR No. : QE-2026-005</span>
      <span style="${redBoxStyle}">Status : Closed</span>
    </div>

    <div style="${sectionStyle}">1.0 Deviation Details :</div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">DR Category</span><span>:</span><span>Production</span></div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">Product Name</span><span>:</span><span>403213 / Paracetamol 50 ml vial</span></div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">Stage</span><span>:</span><span>C</span></div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">Batch No(s)</span><span>:</span><span>400682</span></div>

    <div style="${sectionStyle}">2.0 Deviation Observed in :</div>
    <div style="${fieldRowStyle}">
      <span style="${fieldLabelStyle}">Deviation Type</span><span>:</span><span style="${redBoxStyle}">Process</span>
      <span style="${fieldLabelStyle};margin-left:16px;">Ref. OOS No.(if any)</span><span>:</span><span>N / A</span>
    </div>

    <div style="${sectionStyle}">3.0 Description of Deviation :</div>
    <div style="font-size:12px;line-height:1.5;margin-top:4px;">
      Product: 403213 / Paracetamol 50 ml vial Stage C Batch No 400682 is under execution at Production Block-11 through BPR (MBPR-QE-005, Version no. 00). At Op. No. 44, during the filtration in Agitated Nutsche Filter Dryer #DNFD25-1, material passing was observed in the filtered mother liquors (MLs). This was observed by Mr. Grandhe Sriram, Production Shift In-charge on date 21-Dec-2025.
    </div>

    <div style="${sectionStyle}">4.0 Immediate Action taken (if any) :</div>
    <div style="font-size:12px;line-height:1.5;margin-top:4px;">
      1. Informed to Process Engineering, PR&amp;D, Production head and Quality Assurance Head.<br/>
      2. Filtration activity has been stopped.<br/>
      3. Filtered Mother Liquor with haziness (~90L) is collected in new clean containers and kept aside.
    </div>

    <div style="${sectionStyle}">5.0 Reason, if identified :</div>
    <div style="font-size:12px;line-height:1.5;margin-top:4px;">To be investigated.</div>
  `;
}

function buildChangeControlHTML() {
  return `
    <div style="display:flex;justify-content:space-between;margin-bottom:4px;">
      <div><div style="font-size:16px;font-weight:700;">Oxford Biomedica</div><div style="font-size:10px;color:#888;">Corporate</div></div>
      <div style="font-size:12px;color:#1976d2;font-weight:600;">Oxford Biomedica</div>
    </div>
    <div style="font-size:11px;color:#555;margin-bottom:6px;">Reference SOP No. &amp; Title: 99-13, Change Control</div>
    <div style="text-align:center;font-weight:700;font-size:14px;margin-bottom:10px;">PROCESS/ EQUIPMENT / SITE / SPEC CHANGE REQUEST</div>
    <hr style="border:none;border-top:1px solid #ccc;margin-bottom:8px;"/>

    <div style="display:flex;gap:24px;margin-bottom:8px;">
      <span style="font-size:12px;"><strong>CC No.</strong> : CMO-CC-2026-020</span>
      <span style="font-size:12px;"><strong>Status</strong> : Closed</span>
    </div>

    <div style="${sectionStyle}">1.0 Change Request Details :</div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">CC Category</span><span>:</span><span>Product Change</span></div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">Product name</span><span>:</span><span>403213 / Paracetamol 50 ml vial</span></div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">Stage</span><span>:</span><span>C</span></div>

    <div style="${sectionStyle}">2.0 Proposed Change required in :</div>
    <div style="${fieldRowStyle}"><span style="${fieldLabelStyle}">Change Type</span><span>:</span><span>Process</span></div>

    <div style="margin-top:8px;font-size:12px;">
      <div><strong>Existing Number</strong> : MBPR-QE-001 <span style="text-decoration:line-through;">Version: 00</span></div>
      <div style="margin-top:3px;"><strong>Revised Number</strong> : MBPR-QE-001 Version: 01</div>
    </div>
  `;
}
